import { CarePlusOneTimeBonus, CarePlusPlanDuration, CarePlusPlanFeeBreakdown, CarePlusProductFees } from "./types";

// Care+ price maths shared by Choose Plan, the reorder offer and My Account.

// Figma order.
export const FEE_ROW_ORDER: (keyof CarePlusPlanFeeBreakdown)[] = ["treatment_fee", "service_fee", "shipping_fee"];

export interface BasicPlanPricing {
  /** Monthly fees before the plan bonus. */
  totalFee: number;
  /** Monthly fees after the plan bonus (billed). */
  chargedFee: number;
  /** "Save €X" amount. */
  savings: number;
  hasDiscount: boolean;
  /** Fees the bonus waives entirely. */
  freeFees: (keyof CarePlusPlanFeeBreakdown)[];
  isFirstOrderOnly: boolean;
  /** Fees from month 2 (a first_order bonus has expired). */
  secondMonthFee: number;
  /** Medication + fees before the bonus ("usually"). */
  headerTotal: number;
  /** Medication + fees after the bonus (Total). */
  headerCharged: number;
}

export const computeBasicPlanPricing = (duration: CarePlusPlanDuration | null, medicationPrice: number): BasicPlanPricing => {
  const bonus = duration?.bonus ?? null;
  const totalFee = duration?.total_fee ?? duration?.plan_fee ?? 0;
  const chargedFee = duration?.charged_fee ?? totalFee;
  const hasDiscount = bonus !== null && bonus.discount > 0 && chargedFee < totalFee;
  const isFirstOrderOnly = bonus?.applies_to === "first_order";
  // HL-11489: price the medication in, so a waived fee stack doesn't read as "0 €".
  const includeMedication = medicationPrice > 0;

  return {
    totalFee,
    chargedFee,
    savings: hasDiscount ? bonus!.discount : 0,
    hasDiscount,
    freeFees: FEE_ROW_ORDER.filter((fee) => bonus?.fully_covered_fees?.includes(fee)),
    isFirstOrderOnly,
    secondMonthFee: isFirstOrderOnly ? totalFee : chargedFee,
    headerTotal: includeMedication ? medicationPrice + totalFee : totalFee,
    headerCharged: includeMedication ? medicationPrice + chargedFee : chargedFee,
  };
};

export interface OneTimePricing {
  oneTimePrice: number;
  oneTimeChargedPrice: number;
  hasOneTimeDiscount: boolean;
}

/** Medication + all fees, less the one-time bonus. */
export const computeOneTimePricing = (
  productFees: CarePlusProductFees | null | undefined,
  medicationPrice: number,
  oneTimeBonus: CarePlusOneTimeBonus | null | undefined
): OneTimePricing => {
  const feeTotal =
    Number(productFees?.treatment_fee ?? 0) + Number(productFees?.service_fee ?? 0) + Number(productFees?.shipping_fee ?? 0);
  const oneTimePrice = medicationPrice + feeTotal;
  const oneTimeChargedPrice = Math.max(0, oneTimePrice - Number(oneTimeBonus?.discount ?? 0));

  return {
    oneTimePrice,
    oneTimeChargedPrice,
    hasOneTimeDiscount: oneTimePrice > 0 && oneTimeChargedPrice < oneTimePrice,
  };
};
